import { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Bell, X } from 'lucide-react'
import type { SalonNote, SalonRow } from '@manaro/contracts'
import { Button } from '@/shared/ui/button.tsx'
import { Alert } from '@/shared/ui/states.tsx'
import { findNotesRow, formatDay, notesParam, shiftDays } from '../reminders.ts'
import { markRemindersSeen, reminderKey, useSeenReminders } from '../seen-reminders.ts'
import { useDueReminders, useUpdateSalonNote } from '../use-salon-notes.ts'
import { useSalons } from '../use-salons.ts'
import { errorText, linkButtonClass } from './note-parts.tsx'

interface ReminderItemProps {
  note: SalonNote & { remindOn: string }
  row: SalonRow | null
  today: string
  onOpen: () => void
}

function ReminderItem({ note, row, today, onOpen }: ReminderItemProps) {
  const update = useUpdateSalonNote()
  const title = row ? (row.salon?.title ?? row.user.email) : `Владелец #${note.userId}`
  const subtitle = row?.salon ? row.user.email : null

  const moveTo = (day: string) => update.mutate({ id: note.id, patch: { remindOn: day } })

  return (
    <li className="flex flex-col gap-2 py-3.5">
      <div className="min-w-0">
        <div className="font-semibold [overflow-wrap:anywhere]">{title}</div>
        {subtitle && <div className="text-[12.5px] text-ink-muted [overflow-wrap:anywhere]">{subtitle}</div>}
      </div>
      <p className="line-clamp-4 text-[14.5px] whitespace-pre-line [overflow-wrap:anywhere]">{note.text}</p>
      {note.remindOn < today && (
        <span className="text-[12.5px] font-medium text-warn-ink">Просрочено: напоминание было на {formatDay(note.remindOn)}</span>
      )}
      <div className="flex flex-wrap items-center gap-2 max-sm:[&>button]:min-h-11">
        <Button size="sm" loading={update.isPending} onClick={() => update.mutate({ id: note.id, patch: { reminderDone: true } })}>
          Готово
        </Button>
        <Button variant="secondary" size="sm" disabled={update.isPending} onClick={onOpen}>
          Открыть комментарии
        </Button>
        <span className="flex gap-3 text-[13px]">
          <button type="button" className={linkButtonClass} disabled={update.isPending} onClick={() => moveTo(shiftDays(today, 1))}>
            Завтра
          </button>
          <button type="button" className={linkButtonClass} disabled={update.isPending} onClick={() => moveTo(shiftDays(today, 7))}>
            Через неделю
          </button>
        </span>
      </div>
      {update.error && <Alert>{errorText(update.error)}</Alert>}
    </li>
  )
}

interface RemindersDialogProps {
  notes: Array<SalonNote & { remindOn: string }>
  today: string
  onDismiss: (keys: readonly string[]) => void
}

function RemindersDialog({ notes, today, onDismiss }: RemindersDialogProps) {
  const dialogRef = useRef<HTMLDialogElement>(null)
  const navigate = useNavigate()
  // Names the salons: on another page this loads the list once, then it is cached.
  const rows = useSalons().data?.rows ?? []

  useEffect(() => {
    dialogRef.current?.showModal()
  }, [])

  const dismissAll = () => onDismiss(notes.map(reminderKey))

  return (
    <dialog
      ref={dialogRef}
      onCancel={(event) => {
        event.preventDefault()
        dismissAll()
      }}
      aria-labelledby="reminders-title"
      className="m-auto max-h-[calc(100dvh-24px)] w-[min(520px,calc(100vw-24px))] overflow-y-auto overscroll-contain rounded-panel border border-line bg-surface p-0 text-ink shadow-panel backdrop:bg-[rgb(27_27_24/0.35)]"
    >
      <div className="flex flex-col gap-1 px-5 py-5 md:px-6">
        <header className="flex items-start gap-3">
          <Bell className="mt-1 size-5 shrink-0 text-warn-ink" aria-hidden />
          <div className="min-w-0 flex-1">
            <h2 id="reminders-title" className="font-display text-[19px] leading-tight font-semibold tracking-[-0.01em]">
              {notes.length === 1 ? 'Напоминание' : `Напоминания · ${notes.length}`}
            </h2>
            <p className="mt-0.5 text-[13px] text-ink-muted">Вы просили напомнить об этом на сегодня.</p>
          </div>
          <button
            type="button"
            onClick={dismissAll}
            aria-label="Закрыть до следующего входа"
            title="Закрыть до следующего входа"
            className="-mt-1.5 -mr-2 flex size-11 shrink-0 items-center justify-center rounded-full text-ink-muted transition-colors hover:bg-surface-hover hover:text-ink md:size-9"
          >
            <X className="size-5" aria-hidden />
          </button>
        </header>

        <ul className="divide-y divide-line-soft">
          {notes.map((note) => (
            <ReminderItem
              key={note.id}
              note={note}
              row={findNotesRow(rows, notesParam(note))}
              today={today}
              onOpen={() => {
                onDismiss([reminderKey(note)])
                navigate(`/salons?notes=${notesParam(note)}`)
              }}
            />
          ))}
        </ul>

        <p className="border-t border-line-soft pt-3 text-[12px] text-ink-muted text-balance-pretty">
          Закроете окно — напомним снова при следующем входе, а пока число висит у «Салонов».
        </p>
      </div>
    </dialog>
  )
}

/**
 * Pops up anywhere in the back office when the signed-in person's reminders are due.
 * Closing hides them until the next load; «Готово» and a new day are saved.
 */
export function RemindersWindow({ enabled }: { enabled: boolean }) {
  const { data } = useDueReminders(enabled)
  const seen = useSeenReminders()

  const due = data?.available ? data.due.filter((note): note is SalonNote & { remindOn: string } => note.remindOn !== null) : []
  const visible = due.filter((note) => !seen.has(reminderKey(note)))
  if (!data || visible.length === 0) return null

  return <RemindersDialog notes={visible} today={data.today} onDismiss={markRemindersSeen} />
}
